import { rem } from "@certn/cascadia";
import { css } from "@emotion/css";
import type { AriaListBoxOptions } from "@react-aria/listbox";
import type { Node } from "@react-types/shared";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useListBox, useListBoxSection, useOption } from "react-aria";
import { ComboBoxState } from "react-stately";

import { Divider } from "../../components/divider/Divider";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
interface ListBoxProps extends AriaListBoxOptions<unknown> {
  state: ComboBoxState<unknown>;
  listBoxRef?: React.RefObject<HTMLUListElement>;
  maxHeight?: number;
  hasHighlightOptions?: boolean;
  trailingItem?: React.ReactNode;
  closePanel?: JSX.Element | undefined;
  width?: string;
}
interface SectionProps {
  section: Node<unknown>;
  state: ComboBoxState<unknown>;
}
interface OptionProps {
  item: Node<unknown>;
  state: ComboBoxState<unknown>;
}

/**
 * @deprecated use the new SelectRAC components based on React Aria Components instead
 */
export function ListBoxSection({ section, state }: SectionProps) {
  const { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });

  return (
    <li {...itemProps} className={styles.section.sectionListItem}>
      {section.rendered && (
        <span {...headingProps}>
          <Text
            style="input/section-title"
            className={styles.section.sectionHeader}
          >
            {section.rendered}
          </Text>
        </span>
      )}
      <ul {...groupProps} className={styles.section.sectionWrapper}>
        {[...section.childNodes].map((node) => (
          <Option key={node.key} item={node} state={state} />
        ))}
      </ul>
    </li>
  );
}

export function Option({ item, state }: OptionProps) {
  const ref = React.useRef<HTMLLIElement>(null);
  const { optionProps, isSelected, isFocused } = useOption(
    {
      key: item.key,
    },
    state,
    ref
  );

  return (
    <li
      className={css(styles.listBoxItem, {
        "&:hover": {
          cursor: "pointer",
          background: !isSelected
            ? "var(--background-accent-neutral-weak)"
            : undefined,
        },
        background: isSelected
          ? "var(--background-info-weak)"
          : isFocused
            ? "var(--background-accent-neutral-weak)"
            : undefined,
      })}
      {...optionProps}
      ref={ref}
    >
      <Text style="input/text-field">{item.rendered}</Text>
      {isSelected && <Icon name="check" color="icon-info" />}
    </li>
  );
}

const HighlightOption = ({ item, state }: OptionProps) => {
  const ref = useRef<HTMLLIElement>(null);
  const [parts, setParts] = useState([]);

  useEffect(() => {
    const newParts = parse(
      item.textValue,
      match(item.textValue, state?.inputValue, {
        insideWords: true,
      })
    );

    if (newParts) setParts(newParts as []);
  }, [state, item.textValue]);

  const { optionProps, isSelected, isFocused } = useOption(
    {
      key: item.key,
    },
    state,
    ref
  );

  return (
    <li
      className={css(styles.listBoxItem, {
        "&:hover": {
          cursor: "pointer",
          background: !isSelected
            ? "var(--background-accent-neutral-weak)"
            : undefined,
        },
        background: isSelected
          ? "var(--background-info-weak)"
          : isFocused
            ? "var(--background-accent-neutral-weak)"
            : undefined,
      })}
      {...optionProps}
      ref={ref}
    >
      <div
        className={css({
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        })}
      >
        <div>
          {parts?.map((part: { text: string; highlight: boolean }, index) => {
            const fontWeight = part.highlight ? 700 : 400;
            return (
              <Text
                key={index}
                // the important was required to maintain bold on refresh
                className={css({ fontWeight: `${fontWeight} !important` })}
                style="input/text-field"
              >
                {part.text}
              </Text>
            );
          })}
        </div>
        {isSelected && (
          <div className={css({ float: "right" })}>
            <Icon name="check" color="icon-info" />
          </div>
        )}
      </div>
    </li>
  );
};

export function ListBox(props: ListBoxProps) {
  const ref = useRef<HTMLUListElement>(null);
  const { listBoxRef = ref, state, maxHeight, width = "100%" } = props;
  const { listBoxProps } = useListBox(
    { ...props, shouldFocusOnHover: false },
    state,
    listBoxRef
  );

  const optionsList = [...state.collection].filter(
    (item) =>
      item.type === "item" ||
      (item.type === "section" && item.props["data-sticky"] !== "true")
  );

  const stickySection = [...state.collection].filter(
    (item) => item.type === "section" && item.props["data-sticky"] === "true"
  );

  //next const set bottom margin for listItem to provide enough space for all non-sticky options in select dropdown
  const listItemMargin = stickySection[0]?.props.children && 52;
  const OptionType = props.hasHighlightOptions ? HighlightOption : Option;

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        width: "100%",
      })}
    >
      {props.closePanel}
      <ul
        {...listBoxProps}
        ref={listBoxRef}
        className={css(styles.listBox, {
          width,
          marginBottom: `${listItemMargin}px`,
          maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        })}
      >
        {optionsList.map((item) => {
          return item.type === "section" ? (
            <ListBoxSection key={item.key} section={item} state={state} />
          ) : (
            <OptionType key={item.key} item={item} state={state} />
          );
        })}
      </ul>
      {!!stickySection.length && (
        <div
          className={css(styles.stickyBlock, {
            background: "var(--background-base)",
          })}
        >
          <Divider />
          <div className={styles.stickyWrapper}>
            {stickySection.map((item) => {
              return (
                <ListBoxSection key={item.key} section={item} state={state} />
              );
            })}
          </div>
        </div>
      )}
      {props.trailingItem}
    </div>
  );
}

const styles = {
  section: {
    sectionWrapper: css({
      padding: 0,
      margin: 0,
    }),
    sectionListItem: css({
      listStyleType: "none",
    }),
    sectionHeader: css({
      padding: `${"var(--space-3)"} ${"var(--space-2)"} ${"var(--space-1)"} ${rem(
        "space-2"
      )}`,
      marginBottom: "var(--space-1)",
      display: "inline-block",
    }),
  },
  listBox: css({
    padding: 0,
    margin: 0,
    width: "100%",
    position: "relative",
    overflowY: "auto",

    "&:focus": {
      outline: "none",
    },
  }),
  listBoxItem: css({
    boxSizing: "border-box",
    listStyleType: "none",
    padding: `${"var(--space-2)"} ${"var(--space-2-5)"}`,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "var(--space-1)",
    borderRadius: "var(--border-radius-2)",
    "&:last-child": css({
      marginBottom: 0,
    }),
    "&:hover": {
      cursor: "pointer",
    },
    "&:focus": {
      outline: "none",
    },
  }),
  suggestionWrapper: css({
    padding: "var(--space-3)",
    paddingRight: "var(--space-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  closeIconWrapper: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "&:hover": {
      cursor: "pointer",
    },
  }),
  stickyBlock: css({
    position: "absolute",
    bottom: 8,
    left: 0,
    right: 0,
  }),
  stickyWrapper: css({
    paddingTop: "var(--space-1)",
    paddingRight: "var(--space-1)",
    paddingLeft: "var(--space-1)",
  }),
};
